import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { AngularFireDatabase } from '@angular/fire/database';
import { AngularFireStorage } from '@angular/fire/storage';
import { FlashMessagesService } from '@mobilestar/ngx-flash-messages';
import { AccountService } from '@services/account.service';
import { ModalService } from '@services/support/modal.service';
import { Subscription } from 'rxjs';
import { first, map, switchMap } from 'rxjs/operators';

import { create as contentDisposition } from "content-disposition-header";
import { LoaderService } from '@services/support/loader.service';
import { environment } from 'environments/environment';

@Component({
  selector: 'app-whitelabels',
  templateUrl: './whitelabels.component.html',
  styleUrls: ['./whitelabels.component.scss']
})
export class WhitelabelsComponent implements OnInit, OnDestroy {

  @ViewChild("addForceUpdateTemplate", { static: true }) private addForceUpdateTemplate: TemplateRef<any>;
  @ViewChild("deleteForceUpdateTemplate", { static: true }) private deleteForceUpdateTemplate: TemplateRef<any>;
  @ViewChild("replaceFileTemplate", { static: true }) private replaceFileTemplate: TemplateRef<any>;
  @ViewChild("uploadTemplate", { static: true }) private uploadTemplate: TemplateRef<any>;

  @ViewChild("addLinkTemplate", { static: true }) private addLinkTemplate: TemplateRef<any>;
  @ViewChild("deleteLinkTemplate", { static: true }) private deleteLinkTemplate: TemplateRef<any>;

  @ViewChild("downloadTemplate", { static: true }) private downloadTemplate: TemplateRef<any>;
  @ViewChild("showUrlTemplate", { static: true }) private showUrlTemplate: TemplateRef<any>;

  @ViewChild("markEndedTemplate", { static: true }) private markEndedTemplate: TemplateRef<any>;
  @ViewChild("markActiveTemplate", { static: true }) private markActiveTemplate: TemplateRef<any>;

  @ViewChild("applyForceUpdateTemplate", { static: true }) private applyForceUpdateTemplate: TemplateRef<any>;
  @ViewChild("editInfoTemplate", { static: true }) private editInfoTemplate: TemplateRef<any>;

  showInputs: any = {}
  visibleForceUpdate: string = ""

  whitelabels = []
  whitelabelsSub: Subscription = null
  accountsMetaSub: Subscription = null;

  groupedAccounts = []

  constructor(
    private afdb: AngularFireDatabase,
    private afst: AngularFireStorage,
    private accountService: AccountService,
    private loaderService: LoaderService,
    private modalService: ModalService,
    private flashMessages: FlashMessagesService
  ) { }

  ngOnInit() {
    this.accountService.accountsMeta.pipe(
      first(),
      switchMap(accountsMeta => {
        return this.afdb.object<any>("whitelabels").valueChanges().pipe(map(whitelabelsObj => [accountsMeta, whitelabelsObj]))
      })
    )
    .subscribe(([accountsMeta, whitelabelsObj]) => {
      const whitelabelIds = whitelabelsObj ? Object.keys(whitelabelsObj) : []
      this.whitelabels = whitelabelIds
        .map(wid => {
          const w = whitelabelsObj[wid]
          w.id = wid
          w.downloadLinks = (w.downloadLinks ? Object.keys(w.downloadLinks) : []).map(lid => {
            const l = w.downloadLinks[lid]
            l.id = lid
            return l
          })
          return w
        })
        .sort((a,b) => a.name.localeCompare(b.name))
        .sort((a,b) => {
          return a.ended ? 1 : (b.ended ? -1 : (b.env === environment.environmentName ? 1 : (a.env === environment.environmentName ? -1 : 1)))
        });
      this.groupedAccounts = this.whitelabels.map(w => {
        return { ...w, accounts: accountsMeta.filter(a => a.whitelabel === w.env), hidden: false }
      })
    }, err => console.log(err));
  }

  ngOnDestroy() {
    if (this.whitelabelsSub) { this.whitelabelsSub.unsubscribe() }
  }

  copied(event) {
    this.flashMessages.show("Copied to clipboard.", { cssClass: 'alert-success', timeout: 4000, closeOnClick: true });
  }

  markEnded(whitelabel) {
    const modalId = this.modalService.show({
      template: this.markEndedTemplate,
      context: {
        dataModel: null,
        callbacks: {
          close: () => this.modalService.hide(modalId),
          mark: () => {
            this.afdb.object<any>(`whitelabels/${whitelabel.id}/ended`).set(true)
            this.visibleForceUpdate = ""
            this.modalService.hide(modalId)
          }
        }
      }
    })
  }

  markActive(whitelabel) {
    const modalId = this.modalService.show({
      template: this.markActiveTemplate,
      context: {
        dataModel: null,
        callbacks: {
          close: () => this.modalService.hide(modalId),
          mark: () => {
            this.afdb.object<any>(`whitelabels/${whitelabel.id}/ended`).remove()
            this.visibleForceUpdate = ""
            this.modalService.hide(modalId)
          }
        }
      }
    })
  }

  scrollToElement(envWrapper) {
    envWrapper.scrollIntoView();
  }

  editWhitelabelInfo(fieldName: string, fieldPlaceholder: string, whitelabelId: string, oldValue: string) {
    const dataModel = { fieldPlaceholder: fieldPlaceholder, infoField: oldValue }
    const modalId = this.modalService.show({
      template: this.editInfoTemplate,
      context: {
        dataModel: dataModel,
        callbacks: {
          close: () => this.modalService.hide(modalId),
          save: () => {
            const ref = this.afdb.object(`whitelabels/${whitelabelId}/info/${fieldName}`)
            const promise = dataModel.infoField === '' ? ref.remove() : ref.set(dataModel.infoField)
            promise
              .then(() => this.modalService.hide(modalId))
              .catch(error => {
                console.log(error);
                this.flashMessages.show('Update failed', { cssClass: 'alert-danger', timeout: 4000, closeOnClick: true })
              })
          }
        }
      }
    });
  }

  updateCurrentVersion(whitelabel, currentVersion: string) {
    if (currentVersion.length === 0) {
      return
    }
    this.afdb.object(`whitelabels/${whitelabel.id}/remoteForceUpdate`).update({ currentVersion: currentVersion })
    .catch(error => {
      console.log(error);
      this.flashMessages.show('Update failed', { cssClass: 'alert-danger', timeout: 4000, closeOnClick: true })
    })
  }

  updateCurrentBuild(whitelabel, currentBuild: string) {
    if (currentBuild.length === 0) {
      return
    }
    this.afdb.object(`whitelabels/${whitelabel.id}/remoteForceUpdate`).update({ currentBuild: currentBuild })
    .catch(error => {
      console.log(error);
      this.flashMessages.show('Update failed', { cssClass: 'alert-danger', timeout: 4000, closeOnClick: true })
    })
  }

  updateMinimumVersion(whitelabel, minimumVersion: string) {
    if (minimumVersion.length === 0) {
      return
    }
    this.afdb.object(`whitelabels/${whitelabel.id}/remoteForceUpdate`).update({ minimumVersion: minimumVersion })
    .catch(error => {
      console.log(error);
      this.flashMessages.show('Update failed', { cssClass: 'alert-danger', timeout: 4000, closeOnClick: true })
    })
  }

  updateMinimumBuild(whitelabel, minimumBuild: string) {
    if (minimumBuild.length === 0) {
      return
    }
    this.afdb.object(`whitelabels/${whitelabel.id}/remoteForceUpdate`).update({ minimumBuild: minimumBuild })
    .catch(error => {
      console.log(error);
      this.flashMessages.show('Update failed', { cssClass: 'alert-danger', timeout: 4000, closeOnClick: true })
    })
  }

  updateMarketUrl(whitelabel, marketUrl: string) {
    if (marketUrl.length === 0) {
      return
    }
    this.afdb.object(`whitelabels/${whitelabel.id}/remoteForceUpdate`).update({ marketUrl: marketUrl })
    .catch(error => {
      console.log(error);
      this.flashMessages.show('Update failed', { cssClass: 'alert-danger', timeout: 4000, closeOnClick: true })
    })
  }

  replaceFile(whitelabel) {
    const fileId = this.afdb.createPushId()

    const dataModel = {
      filename: null,
      file: null
    }

    const modalId = this.modalService.show({
      template: this.replaceFileTemplate,
      context: {
        dataModel: dataModel,
        callbacks: {
          close: () => this.modalService.hide(modalId),
          fileChange: (event) => {
            const file: File = event.target.files[0]
            if (file) {
              dataModel.filename = file.name
              dataModel.file = file
            }
          },
          onEnvFileNameClick: (envFile) => {
            if (!dataModel.file) {
              envFile.click()
            }
          },
          deselectFile: (envFile) => {
            dataModel.filename = ""
            dataModel.file = null
            envFile.value = ''
          },
          replace: () => {
            const filePath = `force_update/${whitelabel.id}/${fileId}`
            const oldFilePath = whitelabel.remoteForceUpdate.filePath

            // Upload new file and get download url first
            this.uploadFile(filePath, dataModel.filename, dataModel.file)
              .then(() => this.afst.ref(filePath).getDownloadURL().toPromise())
              .then(url => this.afdb.object(`whitelabels/${whitelabel.id}/remoteForceUpdate`).update({
                filename: dataModel.filename,
                filePath: filePath,
                fileUrl: url
              }))
              // then try to remove the old file
              .then(() => {
                if (oldFilePath) {
                  this.afst.ref(oldFilePath).delete().toPromise()
                    .catch(err => console.log(err))
                }
              })
              .then(() => this.modalService.hide(modalId))
              .catch(err => {
                console.log(err)
                this.flashMessages.show('Replace failed', { cssClass: 'alert-danger', timeout: 4000, closeOnClick: true })
              })
          }
        }
      }
    });
  }

  uploadFile(filePath: string, filename: string, file: File) {
    const uploadTask = this.afst.ref(filePath).put(file, { contentDisposition: contentDisposition(filename), contentType: file.type })

    const dataModel = { progress: 0 }
    const modalId = this.modalService.show({
      template: this.uploadTemplate,
      context: {
        dataModel: dataModel,
        callbacks: {
          cancel: () => {
            uploadTask.cancel()
            this.modalService.hide(modalId)
          }
        }
      }
    })
    uploadTask.percentageChanges().subscribe(progress => { dataModel.progress = progress })
    return uploadTask
      .then(() => new Promise<void>(resolve => setTimeout(() => resolve(), 1000)))
      .then(() => this.modalService.hide(modalId))
      .catch(e => {
        this.modalService.hide(modalId)
        throw e
      })
  }

  addForceUpdateInfo(whitelabel) {
    const fileId = this.afdb.createPushId()

    const dataModel = {
      remoteForceUpdate: {
        currentVersion: "",
        currentBuild: "",
        minimumVersion: "",
        minimumBuild: "",
        marketUrl: "",
        filename: null,
        file: null,
      },
      apk: false
    }

    const modalId = this.modalService.show({
      template: this.addForceUpdateTemplate,
      context: {
        dataModel: dataModel,
        callbacks: {
          close: () => this.modalService.hide(modalId),
          fileChange: (event) => {
            const file: File = event.target.files[0]
            if (file) {
              dataModel.remoteForceUpdate.filename = file.name
              dataModel.remoteForceUpdate.file = file
            }
          },
          onEnvFileNameClick: (envFile) => {
            if (!dataModel.remoteForceUpdate.file) {
              envFile.click()
            }
          },
          deselectFile: (envFile) => {
            dataModel.remoteForceUpdate.filename = ""
            dataModel.remoteForceUpdate.file = null
            envFile.value = ''
          },
          apkChange: (envFile) => {
            if (dataModel.apk) {
              dataModel.remoteForceUpdate.marketUrl = ""
            } else {
              dataModel.remoteForceUpdate.filename = ""
              dataModel.remoteForceUpdate.file = null
              envFile.value = ''
            }
          },
          add: () => {
            if (dataModel.remoteForceUpdate.file) {
              const filePath = `force_update/${whitelabel.id}/${fileId}`
              this.uploadFile(filePath, dataModel.remoteForceUpdate.filename, dataModel.remoteForceUpdate.file)
                .then(() => this.afst.ref(filePath).getDownloadURL().toPromise())
                .then(url => this.afdb.object(`whitelabels/${whitelabel.id}/remoteForceUpdate`).set({
                  currentVersion: dataModel.remoteForceUpdate.currentVersion,
                  currentBuild: dataModel.remoteForceUpdate.currentBuild,
                  minimumVersion: dataModel.remoteForceUpdate.minimumVersion,
                  minimumBuild: dataModel.remoteForceUpdate.minimumBuild,
                  filename: dataModel.remoteForceUpdate.filename,
                  filePath: filePath,
                  fileUrl: url
                }))
                .then(() => {
                  this.visibleForceUpdate = whitelabel.env
                  this.modalService.hide(modalId)
                })
                .catch(err => {
                  console.log(err)
                  this.flashMessages.show('Upload Failed', { cssClass: 'alert-danger', timeout: 4000, closeOnClick: true })
                })
            } else {
              this.afdb.object(`whitelabels/${whitelabel.id}/remoteForceUpdate`).set({
                currentVersion: dataModel.remoteForceUpdate.currentVersion,
                currentBuild: dataModel.remoteForceUpdate.currentBuild,
                minimumVersion: dataModel.remoteForceUpdate.minimumVersion,
                minimumBuild: dataModel.remoteForceUpdate.minimumBuild,
                marketUrl: dataModel.remoteForceUpdate.marketUrl
              })
              .then(() => {
                this.visibleForceUpdate = whitelabel.env
                this.modalService.hide(modalId)
              })
              .catch(err => {
                console.log(err)
                this.flashMessages.show('Upload Failed', { cssClass: 'alert-danger', timeout: 4000, closeOnClick: true })
              })
            }
          }
        }
      }
    });
  }

  deleteForceUpdateInfo(whitelabel) {
    const modalId = this.modalService.show({
      template: this.deleteForceUpdateTemplate,
      context: {
        dataModel: null,
        callbacks: {
          close: () => this.modalService.hide(modalId),
          delete: () => {
            this.loaderService.show()
            let promise
            if (whitelabel.remoteForceUpdate.filePath) {
              promise = this.afst.ref(whitelabel.remoteForceUpdate.filePath).delete().toPromise()
                .then(() => this.afdb.object(`whitelabels/${whitelabel.id}/remoteForceUpdate`).remove())
            } else {
              promise = this.afdb.object(`whitelabels/${whitelabel.id}/remoteForceUpdate`).remove()
            }
            promise
              .catch(err => {
                console.log(err)
                this.flashMessages.show('Delete Failed', { cssClass: 'alert-danger', timeout: 4000, closeOnClick: true })
              })
              .finally(() => {
                this.loaderService.hide()
                this.modalService.hide(modalId)
              })
          }
        }
      }
    })
  }

  applyToAccounts(whitelabel) {
    const url = whitelabel.remoteForceUpdate.marketUrl ? whitelabel.remoteForceUpdate.marketUrl : whitelabel.remoteForceUpdate.fileUrl
    const forceUpdateInfo = {
      cur_name: whitelabel.remoteForceUpdate.currentVersion,
      cur_version: whitelabel.remoteForceUpdate.currentBuild,
      cur_url: url,
      min_name: whitelabel.remoteForceUpdate.minimumVersion,
      min_version: whitelabel.remoteForceUpdate.minimumBuild,
      min_url: url
    }
    const accountIds = whitelabel.accounts.map(a => a.id)

    const modalId = this.modalService.show({
      template: this.applyForceUpdateTemplate,
      context: {
        dataModel: null,
        callbacks: {
          close: () => this.modalService.hide(modalId),
          apply: () => {
            this.loaderService.show()
            this.accountService.applyRemoteForceUpdate(forceUpdateInfo, accountIds)
              .then(() => this.flashMessages.show('Successfully applied.', { cssClass: 'alert-info', timeout: 4000, closeOnClick: true }))
              .catch(err => {
                console.log(err)
                this.flashMessages.show('Failed to apply.', { cssClass: 'alert-danger', timeout: 4000, closeOnClick: true })
              })
              .finally(() => {
                this.loaderService.hide()
                this.modalService.hide(modalId)
              })
          }
        }
      }
    });
  }

  replaceLnk(envId: string, lnk: any) {
    this.showUploadFileModal(envId, lnk.id, "replace", lnk)
  }

  async showActualDownloadUrl(lnk: any) {
    this.loaderService.show()
    const url = await (this.afst.ref(lnk.filePath).getDownloadURL().toPromise())
    this.loaderService.hide()

    const dataModel = { url: url }
    const modalId = this.modalService.show({
      template: this.showUrlTemplate,
      context: {
        dataModel: dataModel,
        callbacks: {
          close: () => this.modalService.hide(modalId),
        }
      }
    });
  }

  addLnk(whitelabelId: string) {
    const linkId = this.afdb.createPushId()

    this.showUploadFileModal(whitelabelId, linkId, "add")
  }

  showUploadFileModal(whitelabelId: string, linkId: string, type: "add" | "replace", link: any = null) {
    const fileId = this.afdb.createPushId()

    const dataModel = { linkPath: link ? link.linkPath : "", filename: null, file: null, type: type }
    const modalId = this.modalService.show({
      template: this.addLinkTemplate,
      context: {
        dataModel: dataModel,
        callbacks: {
          close: () => this.modalService.hide(modalId),
          fileChange: (event) => {
            const file: File = event.target.files[0]
            if (file) {
              dataModel.filename = file.name
              dataModel.file = file
            }
          },
          onLinkFileNameClick: (linkFile) => {
            if (!dataModel.file) {
              linkFile.click()
            }
          },
          deselectFile: (linkFile) => {
            dataModel.filename = ""
            dataModel.file = null
            linkFile.value = '';
          },
          add: () => {
            const filePath = `download_links/${whitelabelId}/${linkId}/${fileId}`
            this.uploadFile2(filePath, dataModel.filename, dataModel.file)
              .then(() => this.afdb.object(`whitelabels/${whitelabelId}/downloadLinks/${linkId}`).set({ filename: dataModel.filename, linkPath: dataModel.linkPath, filePath: filePath }))
              .then(() => this.modalService.hide(modalId))
              .catch(err => {
                console.log(err)
                this.flashMessages.show('Upload Failed', { cssClass: 'alert-danger', timeout: 4000, closeOnClick: true })
              })
          },
          replace: async () => {
            const filePath = `download_links/${whitelabelId}/${linkId}/${fileId}`
            const oldFilePath = link.filePath

            // Upload new file first
            this.uploadFile2(filePath, dataModel.filename, dataModel.file)
              .then(() => this.afdb.object(`whitelabels/${whitelabelId}/downloadLinks/${linkId}`).set({
                filename: dataModel.filename,
                linkPath: dataModel.linkPath,
                filePath: filePath
              }))
              // then try to remove the old file
              .then(() => {
                if (oldFilePath) {
                  this.afst.ref(oldFilePath).delete().toPromise()
                    .catch(err => console.log(err))
                }
              })
              .then(() => this.modalService.hide(modalId))
              .catch(err => {
                console.log(err)
                this.flashMessages.show('Upload Failed', { cssClass: 'alert-danger', timeout: 4000, closeOnClick: true })
              })
          }
        }
      }
    });
  }

  uploadFile2(filePath: string, filename: string, file: File) {
    const uploadTask = this.afst.ref(filePath).put(file, { contentDisposition: contentDisposition(filename), contentType: file.type })

    const dataModel = { progress: 0 }
    const modalId = this.modalService.show({
      template: this.uploadTemplate,
      context: {
        dataModel: dataModel,
        callbacks: {
          cancel: () => {
            uploadTask.cancel()
            this.modalService.hide(modalId)
          }
        }
      }
    })
    uploadTask.percentageChanges().subscribe(progress => { dataModel.progress = progress })
    return uploadTask
      .then(() => new Promise<void>(resolve => setTimeout(() => resolve(), 1000)))
      .then(() => this.modalService.hide(modalId))
      .catch(e => {
        this.modalService.hide(modalId)
        throw e
      })
  }

  deleteLnk(whitelabelId: string, lnk: any) {
    const modalId = this.modalService.show({
      template: this.deleteLinkTemplate,
      context: {
        dataModel: null,
        callbacks: {
          close: () => this.modalService.hide(modalId),
          delete: () => {
            this.loaderService.show()
            let promise
            if (lnk.filePath) {
              promise = this.afst.ref(lnk.filePath).delete().toPromise()
                .then(() => this.afdb.object(`whitelabels/${whitelabelId}/downloadLinks/${lnk.id}`).remove())
            } else {
              promise = this.afdb.object(`whitelabels/${whitelabelId}/downloadLinks/${lnk.id}`).remove()
            }
            promise
              .catch(err => {
                console.log(err)
                this.flashMessages.show('Delete Failed', { cssClass: 'alert-danger', timeout: 4000, closeOnClick: true })
              })
              .finally(() => {
                this.loaderService.hide()
                this.modalService.hide(modalId)
              })
          }
        }
      }
    })
  }

  downloadLnk(lnk: any) {
    this.loaderService.show()
    this.afst.ref(lnk.filePath).getDownloadURL().toPromise()
      .then(url => {
        const dataModel = { url: url }
        const modalId = this.modalService.show({
          template: this.downloadTemplate,
          context: {
            dataModel: dataModel,
            callbacks: {
              close: () => this.modalService.hide(modalId)
            }
          }
        })
      })
      .catch(error => {
        console.log(error)
        this.flashMessages.show('Download link cannot generated', { cssClass: 'alert-danger', timeout: 4000, closeOnClick: true })
      })
      .finally(() => this.loaderService.hide())
  }
}
